"use client";

import { useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { weekIndex, weekPromptPool } from "@/lib/content/moneyTalkPrompts";
import { markMoneyTalkDone } from "@/lib/db/moneyTalks";
import { useAuth } from "@/lib/auth/context";
import { useMembers } from "@/lib/hooks/useMembers";
import { useMoneyTalks } from "@/lib/hooks/useMoneyTalks";
import { ageFromBirthYear } from "@/lib/utils/dates";

type Props = {
  /** Data początku tygodnia (poniedziałek) — wyznacza domyślny prompt. */
  weekStart: Date;
};

/**
 * Karta rozmowy o pieniądzach — pojawia się raz w tygodniu w sheet'cie
 * Friday Recap. Świadomie minimalna: jedno pytanie, jedna wskazówka,
 * "Inne pytanie" jako delikatny opt-out z konkretnego tematu.
 *
 * Pytanie dobierane do wieku najmłodszego dziecka (member.birthYear),
 * kolejne tygodnie idą sekwencyjnie przez program tematyczny.
 *
 * "OK, pogadamy" zapisuje odbytą rozmowę w Firestore (moneyTalks/{weekKey}) —
 * rodzinnie, nie per urządzenie. Po odhaczeniu zostaje cicha linijka
 * z roczną liczbą rozmów: docenienie, nie licznik presji.
 * Brak streaków, brak "nie pogadaliście w zeszłym tygodniu", brak przypomnień.
 */
export function MoneyTalkCard({ weekStart }: Props) {
  const weekKey = weekISOKey(weekStart);
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const uid = firebaseUser?.uid;
  const { members } = useMembers(hid);
  const { talks } = useMoneyTalks(hid);

  // Wiek najmłodszego dziecka — filtruje pulę pytań. Brak birthYear → pełna pula.
  const youngestAge = useMemo(() => {
    const ages = members
      .filter((m) => m.role === "child" && m.birthYear !== undefined)
      .map((m) => ageFromBirthYear(m.birthYear as number));
    return ages.length > 0 ? Math.min(...ages) : undefined;
  }, [members]);

  const pool = useMemo(() => weekPromptPool(youngestAge), [youngestAge]);
  const defaultIndex = weekIndex(weekStart) % pool.length;

  const [shuffledIndex, setShuffledIndex] = useState<number | null>(null);
  const [acceptedLocal, setAcceptedLocal] = useState(false);

  const doneRemote = talks.some((t) => t.weekKey === weekKey);
  // Legacy: stare zapisy localStorage sprzed migracji na Firestore —
  // czytane, żeby karta nie wróciła w tygodniu już odhaczonym na tym urządzeniu.
  const doneLegacy =
    typeof window !== "undefined" &&
    localStorage.getItem(`moneyTalk-accepted-${weekKey}`) === "1";
  const accepted = acceptedLocal || doneRemote || doneLegacy;

  const yearKey = String(weekStart.getFullYear());
  const yearCountRemote = talks.filter((t) =>
    t.weekKey.startsWith(yearKey),
  ).length;
  // Tuż po kliknięciu snapshot jeszcze nie wrócił — dolicz lokalny mark.
  const yearCount =
    acceptedLocal && !doneRemote ? yearCountRemote + 1 : yearCountRemote;

  if (accepted) {
    return (
      <p className="text-center text-[12px] text-muted">
        💬 Rozmowa z tego tygodnia — odbyta
        {yearCount > 1 && <> · w tym roku: {yearCount}</>}
      </p>
    );
  }

  const promptIndex = (shuffledIndex ?? defaultIndex) % pool.length;
  const prompt = pool[promptIndex];

  const handleAccept = () => {
    setAcceptedLocal(true);
    if (hid && uid) {
      void markMoneyTalkDone({
        hid,
        weekKey,
        question: prompt.question,
        uid,
      }).catch(() => {
        // Offline: SDK kolejkuje zapis sam; inny błąd — karta wróci
        // przy następnym otwarciu, nic krytycznego.
      });
    }
  };

  const handleShuffle = () => {
    let next = promptIndex;
    while (next === promptIndex && pool.length > 1) {
      next = Math.floor(Math.random() * pool.length);
    }
    setShuffledIndex(next);
  };

  return (
    <div
      className="relative overflow-hidden rounded-[20px] border border-hay-bd p-5 text-center"
      style={{
        backgroundImage:
          "radial-gradient(circle at 30% 0%, rgba(255,201,60,0.28), transparent 60%), linear-gradient(180deg, var(--kd-hay), color-mix(in srgb, var(--kd-hay) 80%, white))",
      }}
    >
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-hay-bd bg-card px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.05em]"
        style={{ color: "#7A5A00" }}
      >
        💬 Pytanie tygodnia
      </span>

      <p
        className="font-display text-[19px] font-semibold leading-[1.3] text-navy mt-3"
        style={{ letterSpacing: "-0.025em" }}
      >
        {prompt.question}
      </p>

      <p className="text-[12px] text-muted italic mt-2.5">{prompt.hint}</p>

      <div className="flex items-center justify-center gap-2 mt-4">
        <button
          type="button"
          onClick={handleAccept}
          className="flex-1 h-10 rounded-[12px] bg-green text-white text-sm font-semibold"
          style={{ boxShadow: "0 2px 0 var(--kd-forest)" }}
        >
          OK, pogadamy
        </button>
        <button
          type="button"
          onClick={handleShuffle}
          className="h-10 px-3 rounded-[12px] bg-card border border-line text-muted hover:text-navy flex items-center gap-1.5"
          aria-label="Inne pytanie"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          <span className="text-[12px]">Inne</span>
        </button>
      </div>

      <p className="text-[10px] text-muted mt-3 opacity-80">
        Pojawia się raz w tygodniu. Bez streaków i przypomnień.
      </p>
    </div>
  );
}

function weekISOKey(weekStart: Date): string {
  return `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}-${String(weekStart.getDate()).padStart(2, "0")}`;
}
